import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, NavigateFunction } from "react-router-dom";
import { useOktaAuth } from "@okta/okta-react";
import { triggerOktaLogin } from "../../utils/oktaUtil";
import { navigateTo } from "../../../navigateTo";
import {IRIS_ORIGINAL_URI_KEY} from "../root/SecureRoute";
import {LoginContent, RegionHero} from "../../model/RegionLoginContent.model";
import {LoginContentApi} from "../../api/LoginContentApi";
import {LoginLandingCarousel} from "./LoginLandingCarousel.component";

const MAX_IMAGE_PRELOAD_MS = 3000;

const preloadImages = (urls: string[]): Promise<void> => {
    if (urls.length === 0) {
        return Promise.resolve();
    }
    const loadAll = Promise.all(
        urls.map((url) => new Promise<void>((resolve) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => resolve();
            image.src = url;
        }))
    ).then(() => undefined);
    const safetyTimeout = new Promise<void>((resolve) => {
        setTimeout(resolve, MAX_IMAGE_PRELOAD_MS);
    });
    return Promise.race([loadAll, safetyTimeout]);
};

export const LoginLandingPage: React.FunctionComponent = () => {
    const { authState } = useOktaAuth();
    const navigate: NavigateFunction = useNavigate();
    const [searchParams] = useSearchParams();
    const [landingContent, setLandingContent] = useState<LoginContent | null>(null);
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        if (authState?.isAuthenticated) {
            navigateTo("/home", navigate);
        }
    }, [authState?.isAuthenticated, navigate]);

    useEffect(() => {
        let cancelled = false;
        const testCountry = searchParams.get("testCountry") ?? undefined;
        const testLang = searchParams.get("testLang") ?? undefined;
        setIsReady(false);
        LoginContentApi.getPublishedLoginContent(testCountry, testLang)
            .then((content) => {
                if (cancelled) return undefined;
                setLandingContent(content);
                const imageUrls = (content?.carouselCards ?? [])
                    .filter((card) => card.imageDocumentId)
                    .map((card) => LoginContentApi.carouselImageUrl(card.imageDocumentId as number));
                return preloadImages(imageUrls);
            })
            .catch(() => {
                if (!cancelled) setLandingContent(null);
            })
            .finally(() => {
                if (!cancelled) setIsReady(true);
            });
        return () => {
            cancelled = true;
        };
    }, [searchParams]);

    const handleSignIn = () => {
        const storedUri = localStorage.getItem(IRIS_ORIGINAL_URI_KEY);

        let originalUri = storedUri || "/home";
        if (originalUri === "/landing" || originalUri === "/" || originalUri === "/login/callback") {
            originalUri = "/home";
        }

        triggerOktaLogin(originalUri).catch((err) =>
            console.error("Sign-in error:", err)
        );
    };

    const renderAonLogo = () => (
        <div role="img" aria-label="Aon" className="landing-page__aon-logo" />
    );

    const renderPublishedContent = () => {
        if (!landingContent) {
            return null;
        }

        const { hero, stats, servicePills, carouselCards } = landingContent;

        const renderHeroContent = (heroContent: RegionHero) => {
            return <div className="landing-page__hero" data-testid="landing-hero">
                    <p className="landing-page__hero-tagline">{heroContent.tagline}</p>
                    <h2 className="landing-page__hero-headline">{heroContent.headline}</h2>
                    <p className="landing-page__hero-description">{heroContent.description}</p>
                </div>;
        };

        const renderStats = () => {
            return <div className="landing-page__stats" data-testid="landing-stats">
                {stats.map((stat, index) => (
                    <div key={index} className="landing-page__stat">
                        <div className="landing-page__stat-value">{stat.value}</div>
                        <div className="landing-page__stat-label">{stat.label}</div>
                    </div>
                ))}
            </div>;
        };

        const renderServicePills = () => {
            return <div className="landing-page__pills" data-testid="landing-service-pills">
                {servicePills.map((pill, index) => (
                    /* eslint-disable-next-line react/forbid-dom-props */
                    <a key={index} className="landing-page__pill" href={pill.url}
                       target="_blank" rel="noopener noreferrer">
                        {pill.label}
                    </a>
                ))}
            </div>;
        };

        return (
            <div className="landing-page__published-content" data-testid="landing-published-content">
                <div className="landing-page__hero-group">
                    {hero && renderHeroContent(hero)}
                    {carouselCards && carouselCards.length > 0 && <LoginLandingCarousel cards={carouselCards} />}
                    {servicePills.length > 0 && renderServicePills()}
                </div>
                {stats.length > 0 && renderStats()}
            </div>
        );
    };

    if (authState?.isAuthenticated) {
        return null;
    }

    if (!isReady) {
        return null;
    }

    return (
        <div className="landing-page">
            <div className="landing-page__left-panel">
                <div className="landing-page__bg-circle landing-page__bg-circle--1" aria-hidden="true" />
                <div className="landing-page__bg-circle landing-page__bg-circle--2" aria-hidden="true" />
                <div className="landing-page__logo-header">
                    <span className="landing-page__logo-text">AON</span>
                    <span className="landing-page__logo-divider" aria-hidden="true" />
                    <span className="landing-page__logo-iris">Iris</span>
                </div>
                {renderPublishedContent()}
            </div>

            <div className="landing-page__right-panel">
                <div className="landing-page__sign-in-card">
                    <h1 className="landing-page__welcome-title">Welcome Back</h1>
                    <p className="landing-page__welcome-subtitle">Sign in to access your investment insights</p>

                    <div className="landing-page__sign-in-box">
                        {renderAonLogo()}
                        <h2 className="landing-page__sign-in-title">Sign In</h2>
                        <p className="landing-page__sign-in-description">
                            Click below to securely sign in with your Aon credentials
                        </p>

                        <button
                            className="landing-page__sign-in-button"
                            onClick={handleSignIn}
                            data-testid="landing-sign-in-button"
                        >
                            <i className="fa-solid fa-arrow-right-to-bracket" aria-hidden="true" />
                            Continue to Sign In
                        </button>

                        <div className="landing-page__support-link">
                            Need help?{" "}
                            {/* eslint-disable-next-line react/forbid-dom-props */}
                            <a href="mailto:support@aoniris.com">Contact Support</a>
                        </div>
                    </div>

                    <div className="landing-page__secure-notice">
                        <h3 className="landing-page__secure-notice-title">Secure Authentication</h3>
                        <p className="landing-page__secure-notice-text">
                            You'll be redirected to Aon's secure login page to enter your credentials,
                            then returned to Iris.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
